/**
 * Grudge's Most Wanted — graphical novel for Ethereal Treasure.
 * Comic pages for bounty posters + jackpot lore (Durin → Racalvin + Malachar).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FREE_SPIN_BOUNTY,
  JACKPOT_ORDER,
  JACKPOT_TIERS,
  SLOT_MATH,
  type JackpotTierId,
} from "@shared/slot-engine";
import { slotsAudio } from "@/lib/slots-audio";

export type NovelPageId =
  | "cover"
  | "malachar"
  | JackpotTierId
  | "jackpots"
  | "house"
  | "end";

export interface NovelPage {
  id: NovelPageId;
  chapter: string;
  title: string;
  subtitle?: string;
  body: string[];
  poster?: string;
  icon?: string;
  accent: string;
  badge?: string;
}

export function buildWantedNovelPages(): NovelPage[] {
  const cover: NovelPage = {
    id: "cover",
    chapter: "Issue #1",
    title: "GRUDGE'S MOST WANTED",
    subtitle: "Ethereal Treasure · Racalvin Ghost Fleet",
    body: [
      "The ethereal sea is thick with bounties.",
      "Land three matching Wanted posters on the 5×5 glass — claim the crown's coin.",
      "Malachar charts free voyages. Seals open the Ethereal Vault.",
    ],
    poster: "/slots/bounty/racalvin-wanted-sm.jpg",
    accent: "#fbbf24",
    badge: "GRAPHIC NOVEL",
  };

  const malachar: NovelPage = {
    id: "malachar",
    chapter: "Prologue",
    title: FREE_SPIN_BOUNTY.name,
    subtitle: FREE_SPIN_BOUNTY.title,
    body: [
      FREE_SPIN_BOUNTY.note,
      "3 / 4 / 5 bounty charts → free Sea Voyage spins (line mult rises).",
      "Voyage pays lines only — Most Wanted jackpots wait for paid, vault, or special.",
    ],
    poster: FREE_SPIN_BOUNTY.poster,
    icon: FREE_SPIN_BOUNTY.icon,
    accent: "#c4b5fd",
    badge: "FREE SPIN SCATTER",
  };

  const wanted: NovelPage[] = JACKPOT_ORDER.map((id, i) => {
    const t = JACKPOT_TIERS[id];
    const ranks = ["I", "II", "III", "IV"];
    return {
      id,
      chapter: `Bounty ${ranks[i] ?? i + 1}`,
      title: t.name.toUpperCase(),
      subtitle: t.title,
      body: [
        `3+ ${t.name} posters anywhere on the grid pays ${t.mult}× total bet (paid / vault / special).`,
        "4 posters = 1.25× stack · 5 = 1.5× · 7+ = 2×. Free voyage does not pay Wanted.",
        id === "jp_grand"
          ? "Racalvin, Pirate King — the grand bounty of the Ghost Fleet."
          : `Hunt ${t.name} in the vault (bonus) and Racalvin Special reels.`,
      ],
      poster: t.poster,
      icon: t.icon,
      accent: t.color,
      badge: `${t.mult}× JACKPOT`,
    };
  });

  const jackpots: NovelPage = {
    id: "jackpots",
    chapter: "Rules",
    title: "MOST WANTED PAYS",
    subtitle: "Anywhere on 5×5 · not line-bound",
    body: [
      ...JACKPOT_ORDER.map((id) => {
        const t = JACKPOT_TIERS[id];
        return `${t.shortName} · ${t.mult}× bet · ${t.title}`;
      }),
      "Wilds never stand in for Wanted posters, scatters, or seals.",
    ],
    accent: "#fde68a",
    badge: "JACKPOT TABLE",
  };

  const house: NovelPage = {
    id: "house",
    chapter: "Ledger",
    title: "HOUSE MATH",
    subtitle: `Target RTP ${(SLOT_MATH.targetRtp * 100).toFixed(1)}% · Edge ${(SLOT_MATH.houseEdge * 100).toFixed(1)}%`,
    body: [
      `Long-run return band ${Math.round(SLOT_MATH.rtpBand.min * 100)}–${Math.round(SLOT_MATH.rtpBand.max * 100)}%.`,
      `Max win ${SLOT_MATH.maxWinMultiple}× stake per spin (paid or feature).`,
      "Edge is real: symbol weights, feature rates, and hard caps — not client tricks.",
      "Play GBUX ledger only. Variance is high; short sessions swing.",
    ],
    accent: "#94a3b8",
    badge: "OWNER EDGE",
  };

  const end: NovelPage = {
    id: "end",
    chapter: "Spin",
    title: "SET SAIL",
    subtitle: "5×5 · 25 lines · Ethereal Treasure",
    body: [
      "Fullscreen: giant glass, bottom button bar — reels, buttons, noise, and FX only.",
      "Pull the lever or hit SPIN. Auto keeps the voyage going.",
      "Tap Wanted badges anytime to re-open this novel.",
    ],
    poster: "/slots/bounty/racalvin-wanted-sm.jpg",
    accent: "#fbbf24",
    badge: "ENTER CABINET",
  };

  return [cover, malachar, ...wanted, jackpots, house, end];
}

export function WantedGraphicalNovel({
  open,
  onClose,
  startAt,
  awards,
  winTotal,
  mode = "story",
}: {
  open: boolean;
  onClose: () => void;
  /** Jump to a bounty page when opened from a jackpot hit */
  startAt?: NovelPageId;
  awards?: Array<{ tier: JackpotTierId; name: string; count: number; win: number }>;
  winTotal?: number;
  mode?: "story" | "jackpot";
}) {
  const pages = useMemo(() => buildWantedNovelPages(), []);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (mode === "jackpot" && awards?.length) {
      const first = awards[0]!.tier;
      const i = pages.findIndex((p) => p.id === first);
      setIdx(i >= 0 ? i : 0);
    } else if (startAt) {
      const i = pages.findIndex((p) => p.id === startAt);
      setIdx(i >= 0 ? i : 0);
    } else {
      setIdx(0);
    }
    try {
      slotsAudio.unlock();
      if (mode === "jackpot") slotsAudio.win("jackpot");
    } catch {
      /* optional */
    }
  }, [open, startAt, mode, awards, pages]);

  const page = pages[idx] ?? pages[0]!;
  const isLast = idx >= pages.length - 1;
  const isFirst = idx <= 0;

  const next = useCallback(() => {
    if (isLast) onClose();
    else setIdx((v) => Math.min(pages.length - 1, v + 1));
  }, [isLast, onClose, pages.length]);

  const prev = useCallback(() => {
    setIdx((v) => Math.max(0, v - 1));
  }, []);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="wanted-novel"
        className="slots-novel"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        role="dialog"
        aria-label="Grudge Most Wanted graphical novel"
      >
        <div className="slots-novel__scrim" onClick={onClose} aria-hidden />
        <motion.article
          className="slots-novel__book"
          initial={{ scale: 0.88, y: 40 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 20 }}
          transition={{ type: "spring", bounce: 0.35, duration: 0.55 }}
        >
          <header className="slots-novel__head">
            <div>
              <div className="slots-novel__chapter" style={{ color: page.accent }}>
                {page.chapter}
              </div>
              <h2 className="slots-novel__title">{page.title}</h2>
              {page.subtitle ? (
                <p className="slots-novel__sub">{page.subtitle}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="slots-novel__x"
              onClick={onClose}
              aria-label="Close novel"
            >
              ×
            </button>
          </header>

          <div className="slots-novel__spread">
            <div className="slots-novel__panel slots-novel__panel--art">
              {(page.poster || page.icon) && (
                <img
                  src={page.poster || page.icon}
                  alt={page.title}
                  className="slots-novel__poster"
                  draggable={false}
                />
              )}
              {page.badge ? (
                <span
                  className="slots-novel__badge"
                  style={{ borderColor: page.accent, color: page.accent }}
                >
                  {page.badge}
                </span>
              ) : null}
              {mode === "jackpot" && awards?.length ? (
                <div className="slots-novel__hit-strip">
                  {awards.map((a) => (
                    <div key={a.tier} className="slots-novel__hit">
                      <img
                        src={JACKPOT_TIERS[a.tier]?.icon}
                        alt={a.name}
                        draggable={false}
                      />
                      <span>
                        {a.name} ×{a.count}
                      </span>
                      <strong>+{a.win.toLocaleString()}</strong>
                    </div>
                  ))}
                  {winTotal != null ? (
                    <div className="slots-novel__total">
                      +{winTotal.toLocaleString()} GBUX
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="slots-novel__panel slots-novel__panel--text">
              {page.body.map((line, i) => (
                <motion.p
                  key={`${page.id}-${i}`}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                >
                  {line}
                </motion.p>
              ))}
              <div className="slots-novel__wanted-row" aria-hidden>
                {JACKPOT_ORDER.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`slots-novel__thumb ${page.id === id ? "is-on" : ""}`}
                    onClick={() => {
                      const i = pages.findIndex((p) => p.id === id);
                      if (i >= 0) setIdx(i);
                    }}
                    title={JACKPOT_TIERS[id].name}
                  >
                    <img
                      src={JACKPOT_TIERS[id].icon}
                      alt={JACKPOT_TIERS[id].name}
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <footer className="slots-novel__foot">
            <button
              type="button"
              className="slots-novel__nav"
              disabled={isFirst}
              onClick={prev}
            >
              ← Prev
            </button>
            <div className="slots-novel__dots" aria-hidden>
              {pages.map((p, i) => (
                <span
                  key={p.id}
                  className={i === idx ? "is-on" : ""}
                  style={i === idx ? { background: page.accent } : undefined}
                />
              ))}
            </div>
            <button type="button" className="slots-novel__nav primary" onClick={next}>
              {isLast ? "Play" : "Next →"}
            </button>
          </footer>
        </motion.article>
      </motion.div>
    </AnimatePresence>
  );
}
